// The two search bands on every Find row and preview (Neil, 11 Oct 2026: "Needs to give them
// something to search better with that is on the anonymous side"):
//   Opportunity  how the property compares with every building we cover (Top 10% / Strong / Good / Fair)
//   Grid         spare grid capacity for the area around it (good / limited / constrained / not checked)
// Bands only: the API never sends the score, the substation or its figures, and the wording never
// says where the data comes from or how it's worked out (CLAUDE.md). The hover text is the API's
// own explanation (facets.explain) when there is one, else the same words kept here.
//
// Four more (Neil, 11 Oct 2026): existing solar, roof size band, energy rating band, distance band
// from the company's base postcode. Roof and energy rating show "Not shown" where the API hides
// either to keep a building anonymous (each on its own: fewer than 5 buildings share the card's
// district, type, size and that band).
//
// Neil, 11 Oct 2026 ("make it so that they can't guess too much"): payback and grid also show
// "Not shown" where fewer than 5 share the whole card (district, type, size, payback, data, grid),
// and a card whose sector fewer than 5 share in its district and size shows a generic building
// ("Public building") with "Sector not shown" (common/shortlist.py, ANON_MIN).
import { EyeOff, Gauge, PlugZap, Route, Ruler, Sun, TrendingUp } from "lucide-react";
import type React from "react";
import type { DistanceBand, EnergyBand, GridBand, OpportunityBand, RoofBand, ShortlistBand, ShortlistBuilding, SolarBand } from "../api";

export const OPPORTUNITY_TIP = "How good an opportunity this looks: size, energy use and likely return, compared with every building we cover.";
export const GRID_TIP =
  "Spare capacity on the local electricity network for new generation, for the area around the property, compared with the indicative system size. " +
  "A guide only: the network's connection offer decides. Not checked means we don't cover that area yet.";

/** Opportunity badge: a trend icon and the band's label, coloured strongest to weakest. */
export function OpportunityBadge({ value, tip }: { value?: ShortlistBand<OpportunityBand> | null; tip?: string }) {
  if (!value) return <span className="muted">—</span>;
  return (
    <span className={`find-data find-band find-opp-${value.band}`} title={tip ?? OPPORTUNITY_TIP}>
      <TrendingUp size={12} aria-hidden="true" /> {value.label}
    </span>
  );
}

/** Grid badge: a plug icon and the band's label (green good, amber limited, red constrained, grey not checked; "Not shown" when hidden). */
export function GridBadge({ value, tip, hiddenTip }: { value?: ShortlistBand<GridBand | "hidden"> | null; tip?: string; hiddenTip?: string }) {
  if (!value) return <span className="muted">—</span>;
  if (value.band === "hidden") return <Hidden tip={hiddenTip} />;
  return (
    <span className={`find-data find-band find-grid-${value.band}`} title={tip ?? GRID_TIP}>
      <PlugZap size={12} aria-hidden="true" /> {value.label}
    </span>
  );
}

export const SOLAR_TIP = "A hint, not a check: solar panels are mapped on this building's own roof, so some may already be fitted. No solar recorded doesn't mean there are none. Every roof is checked by our roof scan and our team before the report.";
export const ROOF_TIP =
  "The roof's size as a band: small under 500 m², medium 500–1,999 m², large 2,000–4,999 m², very large 5,000 m² and over. The report measures the usable area.";
export const ENERGY_TIP = "How energy efficient the building is rated, from A (best) to G. A lower rating usually means higher energy use.";
export const DISTANCE_TIP = "How far the property is from your base postcode, as a band. Set your base in Account → Profile.";
export const HIDDEN_TIP = "Hidden to keep this building anonymous";

/** "Not shown": the API hid this band so the card can't single the building out. */
function Hidden({ tip }: { tip?: string }) {
  return (
    <span className="find-data find-band find-hidden" title={tip ?? HIDDEN_TIP}>
      <EyeOff size={12} aria-hidden="true" /> Not shown
    </span>
  );
}

/** The payback band as plain text, or "Not shown" (the API sends that label when the anonymity rule hides it). */
export function PaybackValue({ value, hiddenTip }: { value?: string | null; hiddenTip?: string }) {
  if (value === "Not shown") return <Hidden tip={hiddenTip} />;
  return <>{value ?? "—"}</>;
}

/**
 * The sectors' short labels on Find and the preview (BUILD-PLAN-PRIVATE-COMMERCIAL.md, data contract).
 * Public ones as before ("Public offices" is now "Offices": private offices share it); industrial no
 * longer includes warehouses, and retail no longer includes hotels.
 */
export const SECTOR_LABELS: Record<string, string> = {
  education: "Education",
  health: "Health and care",
  leisure: "Leisure and sport",
  offices: "Offices",
  culture: "Culture",
  emergency: "Emergency services",
  industrial: "Factories and workshops",
  warehouses: "Warehouses and distribution",
  retail: "Shops and retail",
  hotels: "Hotels and hospitality",
  other: "Other",
};

/** A generic card's type when the API hasn't named it: by ownership (private: "Business premises"). */
export const genericType = (ownership?: string | null) => (ownership === "private" ? "Business premises" : "Public building");

/** Under the building type: the sector's label, or "Sector not shown" on a generic card (sector null). */
export function SectorNote({ sector, labels, hiddenTip }: { sector?: string | null; labels: Record<string, string>; hiddenTip?: string }) {
  if (!sector) {
    return (
      <span className="find-sector-hidden" title={hiddenTip ?? HIDDEN_TIP}>
        <EyeOff size={12} aria-hidden="true" /> Sector not shown
      </span>
    );
  }
  return <>{labels[sector] ?? sector}</>;
}

/** Existing solar: amber when recorded (a warning), neutral otherwise (never claims there's none). */
export function SolarBadge({ value, tip }: { value?: ShortlistBand<SolarBand> | null; tip?: string }) {
  if (!value) return <span className="muted">—</span>;
  return (
    <span className={`find-data find-band find-solar-${value.band}`} title={tip ?? SOLAR_TIP}>
      <Sun size={12} aria-hidden="true" /> {value.label}
    </span>
  );
}

/** Roof size band ("Large roof"), never the area. */
export function RoofBadge({ value, tip, hiddenTip }: { value?: ShortlistBand<RoofBand | "not-known" | "hidden"> | null; tip?: string; hiddenTip?: string }) {
  if (!value) return <span className="muted">—</span>;
  if (value.band === "hidden") return <Hidden tip={hiddenTip} />;
  return (
    <span className={`find-data find-band find-roof-${value.band}`} title={tip ?? ROOF_TIP}>
      <Ruler size={12} aria-hidden="true" /> {value.label}
    </span>
  );
}

/** Energy rating band ("Energy rating D"). */
export function EnergyBadge({ value, tip, hiddenTip }: { value?: ShortlistBand<EnergyBand | "not-known" | "hidden"> | null; tip?: string; hiddenTip?: string }) {
  if (!value) return <span className="muted">—</span>;
  if (value.band === "hidden") return <Hidden tip={hiddenTip} />;
  return (
    <span className={`find-data find-band find-energy${value.band === "not-known" ? "-not-known" : ""}`} title={tip ?? ENERGY_TIP}>
      <Gauge size={12} aria-hidden="true" /> {value.label}
    </span>
  );
}

/** Distance band from the base postcode; a dash when the company hasn't set one. */
export function DistanceBadge({ value, tip }: { value?: ShortlistBand<DistanceBand | "not-known"> | null; tip?: string }) {
  if (!value) return <span className="muted">—</span>;
  return (
    <span className={`find-data find-band find-distance${value.band === "not-known" ? "-not-known" : ""}`} title={tip ?? DISTANCE_TIP}>
      <Route size={12} aria-hidden="true" /> {value.label}
    </span>
  );
}

// ─── At a glance: compact coloured icons for the Find table (Neil, 11 Oct 2026: "less columns, use of
// coloured icons with legend above the table; only show relevant ones") ─────────────────────────────
// Each icon only appears when it says something: grid always (a key fact), roof and rating when known,
// the sun only when solar may already be fitted, "Est." only for estimated data, an eye where a value is
// hidden to keep the building anonymous. The legend lists only the icons in the current results.

const ROOF_SHORT: Record<string, string> = { small: "S", medium: "M", large: "L", "very-large": "XL" };

/** The signals one row shows, as keys the legend can collect. */
export function rowSignals(b: ShortlistBuilding): string[] {
  const out: string[] = [];
  if (b.grid) out.push(b.grid.band === "hidden" ? "hidden" : `grid-${b.grid.band}`);
  if (b.roof && ROOF_SHORT[b.roof.band]) out.push("roof");
  if (!b.sector || b.roof?.band === "hidden" || b.energyRating?.band === "hidden" || b.grid?.band === "hidden" || b.payback === "Not shown") out.push("hidden");
  if (b.energyRating && /^[A-G]$/.test(b.energyRating.band)) out.push("rating");
  if (b.solar?.band === "yes") out.push("solar");
  if (b.electricityData === "estimated") out.push("estimated");
  return out;
}

/** The row's icons, each with its meaning on hover. */
export function GlanceIcons({ b, tips }: { b: ShortlistBuilding; tips: { grid: string; roof: string; energy: string; solar: string; hidden: string } }) {
  // The sector, roof, rating, grid or payback hidden to keep the building anonymous
  const hidden = !b.sector || b.roof?.band === "hidden" || b.energyRating?.band === "hidden" || b.grid?.band === "hidden" || b.payback === "Not shown";
  return (
    <span className="glance">
      {b.grid && b.grid.band !== "hidden" && (
        <span className={`glance-ico glance-grid-${b.grid.band}`} title={`${b.grid.label}. ${tips.grid}`} aria-label={b.grid.label}>
          <PlugZap size={13} aria-hidden="true" />
        </span>
      )}
      {b.roof && ROOF_SHORT[b.roof.band] && (
        <span className="glance-ico glance-roof" title={`${b.roof.label}. ${tips.roof}`} aria-label={b.roof.label}>
          {ROOF_SHORT[b.roof.band]}
        </span>
      )}
      {b.energyRating && /^[A-G]$/.test(b.energyRating.band) && (
        <span className={`glance-ico glance-rating glance-rating-${b.energyRating.band}`} title={`${b.energyRating.label}. ${tips.energy}`} aria-label={b.energyRating.label}>
          {b.energyRating.band}
        </span>
      )}
      {b.solar?.band === "yes" && (
        <span className="glance-ico glance-solar" title={`${b.solar.label}. ${tips.solar}`} aria-label={b.solar.label}>
          <Sun size={13} aria-hidden="true" />
        </span>
      )}
      {b.electricityData === "estimated" && (
        <span className="glance-ico glance-est" title="Estimated electricity use, not actual figures" aria-label="Estimated data">
          Est.
        </span>
      )}
      {hidden && (
        <span className="glance-ico glance-hidden" title={tips.hidden} aria-label="Some details hidden">
          <EyeOff size={13} aria-hidden="true" />
        </span>
      )}
    </span>
  );
}

/** Above the table: what each icon in these results means (only the ones that appear). */
export function GlanceLegend({ rows }: { rows: ShortlistBuilding[] }) {
  const seen = new Set(rows.flatMap(rowSignals));
  const items: [string, React.ReactNode, string][] = [
    ["grid-good", <span className="glance-ico glance-grid-good"><PlugZap size={13} /></span>, "Good grid capacity"],
    ["grid-limited", <span className="glance-ico glance-grid-limited"><PlugZap size={13} /></span>, "Limited grid capacity"],
    ["grid-constrained", <span className="glance-ico glance-grid-constrained"><PlugZap size={13} /></span>, "Grid constrained"],
    ["grid-not-checked", <span className="glance-ico glance-grid-not-checked"><PlugZap size={13} /></span>, "Grid not checked"],
    ["roof", <span className="glance-ico glance-roof">L</span>, "Roof size: S, M, L, XL"],
    ["rating", <span className="glance-ico glance-rating glance-rating-D">D</span>, "Energy rating, A (best) to G"],
    ["solar", <span className="glance-ico glance-solar"><Sun size={13} /></span>, "Solar may already be fitted"],
    ["estimated", <span className="glance-ico glance-est">Est.</span>, "Estimated electricity use"],
    ["hidden", <span className="glance-ico glance-hidden"><EyeOff size={13} /></span>, "Some details hidden to keep it anonymous"],
  ];
  const shown = items.filter(([k]) => seen.has(k));
  if (!shown.length) return null;
  return (
    <ul className="glance-legend" aria-label="What the icons mean">
      {shown.map(([k, icon, text]) => (
        <li key={k}>
          <span aria-hidden="true">{icon}</span> {text}
        </li>
      ))}
    </ul>
  );
}
